/**
 * COLLECTION STORE
 *
 * Shape (persisted under the key "collection"):
 *   owned      Record<cardId, number>   how many copies the player owns
 *   newCards   string[]                 card ids not yet seen in COLLECTION
 *   unlockAll  boolean                  prototype toggle, BLUEPRINT section 20
 *
 * While `unlockAll` is on, `countOf` reports MAX_COPIES for every card, so the
 * deck builder can build anything without opening packs. The underlying `owned`
 * map is untouched, so turning the toggle off restores the real collection.
 *
 * Other engineers: STORE calls `grant` when a pack is opened; COLLECTION reads
 * `countOf` and clears `newCards` with `markSeen`.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MAX_COPIES } from '@/engine';
import { adapterStorage } from '@/persist';

export interface CollectionState {
  owned: Record<string, number>;
  newCards: string[];
  unlockAll: boolean;
}

export interface CollectionActions {
  /** Add copies. Returns the number of copies that exceeded MAX_COPIES. */
  grant(cardId: string, count?: number): number;
  grantMany(cardIds: string[]): void;
  countOf(cardId: string): number;
  /** Copies actually owned, ignoring the unlockAll toggle. */
  rawCountOf(cardId: string): number;
  setUnlockAll(value: boolean): void;
  markSeen(cardIds?: string[]): void;
  reset(): void;
}

export const DEFAULT_COLLECTION: CollectionState = {
  owned: {},
  newCards: [],
  unlockAll: true,
};

export type CollectionStore = CollectionState & CollectionActions;

export const useCollection = create<CollectionStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_COLLECTION,
      grant(cardId, count = 1) {
        const owned = { ...get().owned };
        const before = owned[cardId] ?? 0;
        const after = Math.min(MAX_COPIES, before + count);
        owned[cardId] = after;
        const seen = new Set(get().newCards);
        if (after > before) seen.add(cardId);
        set({ owned, newCards: [...seen] });
        return before + count - after;
      },
      grantMany(cardIds) {
        const owned = { ...get().owned };
        const seen = new Set(get().newCards);
        for (const id of cardIds) {
          const before = owned[id] ?? 0;
          const after = Math.min(MAX_COPIES, before + 1);
          owned[id] = after;
          if (after > before) seen.add(id);
        }
        set({ owned, newCards: [...seen] });
      },
      countOf(cardId) {
        if (get().unlockAll) return MAX_COPIES;
        return get().owned[cardId] ?? 0;
      },
      rawCountOf(cardId) {
        return get().owned[cardId] ?? 0;
      },
      setUnlockAll(value) {
        set({ unlockAll: value });
      },
      markSeen(cardIds) {
        if (!cardIds) {
          set({ newCards: [] });
          return;
        }
        const drop = new Set(cardIds);
        set({ newCards: get().newCards.filter((id) => !drop.has(id)) });
      },
      reset() {
        set({ ...DEFAULT_COLLECTION });
      },
    }),
    {
      name: 'collection',
      storage: adapterStorage<CollectionStore>(),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<CollectionState> | undefined),
      }),
    },
  ),
);
